import { api, API_URL, apiFetch } from '@/core/api/client'

export interface PBCertificate {
  id: string
  attendeeId: string
  certificateNumber: string
  issuedAt: string
  expiresAt: string
  verificationToken: string
  revoked: boolean
  revokedAt?: string | null
  revokedReason?: string
  emailSentAt?: string | null
  emailError?: string
  attendeeDetail?: {
    id: string
    sessionId: string
    studentId: string
    firstName: string
    lastName: string
    phone: string
    company: string
    email: string
  } | null
}

const certificatesCollection = api.collection<PBCertificate>('certificates')

/** Fetch all certificates. */
export async function getAllCertificates(): Promise<PBCertificate[]> {
  return certificatesCollection.getFullList({ sort: '-issuedAt' })
}

/** Look up a certificate by its public verification token. */
export async function getCertificateByToken(token: string): Promise<PBCertificate | null> {
  try {
    const all = await certificatesCollection.getFullList()
    return all.find((c) => c.verificationToken === token) ?? null
  } catch {
    return null
  }
}

/** Fetch a single certificate by ID. */
export async function getCertificate(id: string): Promise<PBCertificate> {
  return certificatesCollection.getOne(id)
}

/** Create a new certificate record. */
export async function createCertificate(data: {
  attendeeId: string
  certificateNumber: string
  issuedAt: string
  expiresAt: string
  verificationToken: string
  revoked: boolean
}): Promise<PBCertificate> {
  return certificatesCollection.create(data)
}

/** Revoke or reinstate a certificate. Revoking requires a reason. */
export async function revokeCertificate(
  id: string,
  opts: { revoked: boolean; reason?: string }
): Promise<void> {
  const res = await apiFetch(`/certificates/${id}/revoke/`, {
    method: 'POST',
    body: JSON.stringify({ revoked: opts.revoked, reason: opts.reason ?? '' }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.detail || 'Failed to update certificate')
  }
}

/** Re-render certificate HTML/PDF after attendee data corrections (keeps number/issued date). */
export async function reRenderCertificate(id: string): Promise<void> {
  const res = await apiFetch(`/certificates/${id}/render/`, { method: 'POST' })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.detail || 'Failed to re-render certificate')
  }
}

/** Re-send the certificate PDF email to the attendee. */
export async function sendCertificateEmail(id: string): Promise<{ detail: string; renderId?: string; status?: string }> {
  const res = await apiFetch(`/certificates/${id}/send_email/`, { method: 'POST' })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.detail || 'Failed to send certificate email')
  }
  return res.json() as Promise<{ detail: string; renderId?: string; status?: string }>
}

// ---------------------------------------------------------------------------
// Public verification view – read-only, no auth required
// ---------------------------------------------------------------------------

/** Shape returned by the public certificate verification endpoint. */
export interface PBCertVerification {
  certificateNumber: string
  issuedAt: string
  expiresAt: string
  firstName: string
  lastName: string
  courseId: string
  courseName: string
  instructorName: string
  courseColor: string
}

/**
 * Look up the public verification view by token.
 * This queries the `certificate_verifications` view which joins certificates,
 * attendees, and sessions and only exposes the fields needed for verification.
 * No authentication is required and the underlying collections stay locked.
 */
export async function getCertVerification(token: string): Promise<PBCertVerification | null> {
  try {
    const res = await fetch(`${API_URL}/verify/${token}/`)
    if (!res.ok) {return null}
    return res.json()
  } catch {
    return null
  }
}
